// Shared Inline Styles Module — Premium Green Theme with Enhanced Design
// All components import from here instead of CSS files

const colors = {
    primary: '#059669',
    primaryLight: '#10b981',
    primaryDark: '#047857',
    secondary: '#f59e0b',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    purple: '#8b5cf6',
    pink: '#ec4899',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textLight: '#94a3b8',
    bgPrimary: '#f0fdf4',
    bgCard: '#ffffff',
    border: '#e2e8f0',
    white: '#ffffff',
    // Enhanced gradients
    gradientPrimary: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    gradientSecondary: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
    gradientInfo: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
    gradientPurple: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
    gradientSuccess: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
    gradientDanger: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
};

const font = "'Outfit', sans-serif";

// ===== PAGE LAYOUTS =====
export const pageHeader = {
    marginBottom: 24,
};

export const pageTitle = {
    fontSize: 28,
    fontWeight: 800,
    color: colors.textPrimary,
    fontFamily: font,
    margin: 0,
    letterSpacing: -0.5,
};

export const pageSubtitle = {
    fontSize: 15,
    color: colors.textSecondary,
    margin: '8px 0 0',
    fontFamily: font,
    fontWeight: 500,
};

export const pageHeaderRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 24,
};

// ===== STAT CARDS =====
const statGradients = {
    primary: 'linear-gradient(135deg, #059669, #10b981)',
    secondary: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    success: 'linear-gradient(135deg, #10b981, #34d399)',
    info: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
    purple: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
    pink: 'linear-gradient(135deg, #ec4899, #f472b6)',
};

export const gridStats = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 20,
    marginBottom: 28,
};

export const statCard = (variant = 'primary') => ({
    background: colors.white,
    borderRadius: 20,
    padding: '24px 28px',
    border: '1px solid rgba(5, 150, 105, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
});

export const statIcon = (variant = 'primary') => ({
    width: 52,
    height: 52,
    borderRadius: 16,
    background: statGradients[variant] || statGradients.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    fontSize: 22,
    marginBottom: 16,
    boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
});

export const statValue = {
    fontSize: 32,
    fontWeight: 800,
    color: colors.textPrimary,
    fontFamily: font,
    lineHeight: 1.1,
    letterSpacing: -1,
};

export const statLabel = {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 8,
};

// ===== CARDS =====
export const card = {
    background: colors.white,
    borderRadius: 20,
    border: '1px solid rgba(5, 150, 105, 0.08)',
    overflow: 'hidden',
    marginBottom: 20,
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    transition: 'all 0.3s ease',
};

export const cardHeader = {
    padding: '18px 24px',
    borderBottom: '1px solid rgba(5, 150, 105, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: font,
    fontWeight: 700,
    fontSize: 16,
    color: colors.textPrimary,
    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.02) 0%, rgba(16, 185, 129, 0.02) 100%)',
};

export const cardBody = {
    padding: 24,
};

// ===== TABLES =====
export const table = {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: font,
    fontSize: 14,
    tableLayout: 'auto',
};

export const th = {
    padding: '14px 20px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 11,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '2px solid rgba(5, 150, 105, 0.1)',
    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)',
};

export const td = {
    padding: '14px 20px',
    borderBottom: '1px solid rgba(5, 150, 105, 0.06)',
    color: colors.textPrimary,
    fontSize: 14,
};

// ===== BUTTONS =====
export const btnPrimary = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    color: colors.white,
    border: 'none',
    borderRadius: 14,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: font,
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 16px rgba(5, 150, 105, 0.3)',
};

export const btnOutline = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'transparent',
    color: colors.primary,
    border: '2px solid rgba(5, 150, 105, 0.3)',
    borderRadius: 14,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: font,
    transition: 'all 0.2s ease',
};

export const btnSmall = {
    padding: '8px 16px',
    fontSize: 13,
    borderRadius: 10,
    fontWeight: 600,
};

export const btnDanger = {
    ...btnOutline,
    color: colors.danger,
    border: '2px solid rgba(239, 68, 68, 0.3)',
};

// ===== STATUS BADGES =====
const badgeColors = {
    draft: { bg: 'rgba(107,114,128,0.1)', color: '#6b7280' },
    submitted: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6' },
    approved: { bg: 'rgba(16,185,129,0.1)', color: '#10b981' },
    rejected: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444' },
    live: { bg: 'rgba(255,111,0,0.1)', color: '#ff6f00' },
    DRAFT: { bg: 'rgba(107,114,128,0.1)', color: '#6b7280' },
    LOCKED: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6' },
    REVEAL: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b' },
    LIVE: { bg: 'rgba(255,111,0,0.1)', color: '#ff6f00' },
    ENDED: { bg: 'rgba(107,114,128,0.1)', color: '#6b7280' },
    EVALUATION: { bg: 'rgba(139,92,246,0.1)', color: '#8b5cf6' },
    RESULT: { bg: 'rgba(16,185,129,0.1)', color: '#10b981' },
    internal: { bg: 'rgba(20,184,166,0.1)', color: '#14b8a6' },
    semester: { bg: 'rgba(139,92,246,0.1)', color: '#8b5cf6' },
};

export const badge = (status) => {
    const c = badgeColors[status] || badgeColors.draft;
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 12px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.color,
        fontFamily: font,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    };
};

// ===== FORMS =====
export const formGroup = {
    marginBottom: 20,
};

export const label = {
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 8,
    fontFamily: font,
};

export const input = {
    width: '100%',
    padding: '14px 18px',
    border: '2px solid rgba(5, 150, 105, 0.1)',
    borderRadius: 14,
    fontSize: 15,
    fontFamily: font,
    color: colors.textPrimary,
    background: colors.white,
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
};

export const select = {
    ...input,
    appearance: 'none',
    cursor: 'pointer',
};

export const textarea = {
    ...input,
    resize: 'vertical',
    minHeight: 100,
};

// ===== MODALS =====
export const modalOverlay = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.6)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 20,
};

export const modalContent = {
    background: colors.white,
    borderRadius: 24,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    border: '1px solid rgba(5, 150, 105, 0.1)',
};

export const modalHeader = {
    padding: '22px 28px',
    borderBottom: '1px solid rgba(5, 150, 105, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

export const modalTitle = {
    fontSize: 20,
    fontWeight: 800,
    color: colors.textPrimary,
    fontFamily: font,
    margin: 0,
};

export const modalBody = {
    padding: 28,
};

export const modalFooter = {
    padding: '18px 28px',
    borderTop: '1px solid rgba(5, 150, 105, 0.1)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
};

export const closeBtn = {
    background: 'none',
    border: 'none',
    fontSize: 22,
    cursor: 'pointer',
    color: colors.textSecondary,
    padding: 6,
    borderRadius: 10,
    display: 'flex',
    transition: 'all 0.2s ease',
};

// ===== EMPTY STATES =====
export const emptyState = {
    textAlign: 'center',
    padding: '56px 28px',
};

export const emptyIcon = {
    fontSize: 56,
    marginBottom: 20,
    display: 'block',
};

export const emptyTitle = {
    fontSize: 20,
    fontWeight: 700,
    color: colors.textPrimary,
    fontFamily: font,
    marginBottom: 10,
};

export const emptyText = {
    fontSize: 15,
    color: colors.textSecondary,
    fontFamily: font,
    margin: 0,
};

// ===== LOADING =====
export const loadingContainer = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    gap: 20,
};

export const spinner = {
    width: 48,
    height: 48,
    border: '4px solid rgba(5, 150, 105, 0.1)',
    borderTopColor: colors.primary,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
};

export const loadingText = {
    fontSize: 15,
    color: colors.textSecondary,
    fontFamily: font,
    fontWeight: 500,
};

// ===== GRIDS =====
export const grid2 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 24,
    marginBottom: 28,
};

export const grid3 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 20,
    marginBottom: 28,
};

// ===== TABS =====
export const tabsContainer = {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 28,
    padding: 6,
    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
    borderRadius: 16,
    border: '1px solid rgba(5, 150, 105, 0.08)',
};

export const tabBtn = (isActive) => ({
    padding: '10px 20px',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: isActive ? 700 : 600,
    cursor: 'pointer',
    fontFamily: font,
    background: isActive ? colors.white : 'transparent',
    color: isActive ? colors.primary : colors.textSecondary,
    boxShadow: isActive ? '0 2px 10px rgba(5, 150, 105, 0.15)' : 'none',
    transition: 'all 0.2s ease',
});

// ===== ALERT / ERROR =====
export const alertError = {
    padding: '14px 20px',
    borderRadius: 14,
    background: 'rgba(239,68,68,0.06)',
    border: '1px solid rgba(239,68,68,0.15)',
    color: colors.danger,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: font,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
};

export const alertSuccess = {
    ...alertError,
    background: 'rgba(16,185,129,0.06)',
    border: '1px solid rgba(16,185,129,0.15)',
    color: colors.success,
};

// ===== SUBJECT CARDS =====
export const subjectCard = {
    background: colors.white,
    borderRadius: 20,
    border: '1px solid rgba(5, 150, 105, 0.08)',
    padding: 28,
    textAlign: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
};

export const subjectIcon = {
    width: 60,
    height: 60,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    fontSize: 24,
    margin: '0 auto 18px',
    boxShadow: '0 6px 20px rgba(5, 150, 105, 0.25)',
};

// ===== EXAM SPECIFIC =====
export const examTimerStyle = (timerClass) => {
    const bg = timerClass === 'danger' ? 'rgba(239,68,68,0.1)' :
        timerClass === 'warning' ? 'rgba(245,158,11,0.1)' : 'rgba(5,150,105,0.1)';
    const color = timerClass === 'danger' ? '#ef4444' :
        timerClass === 'warning' ? '#f59e0b' : '#059669';
    return {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 20px',
        borderRadius: 14,
        background: bg,
        color,
        fontWeight: 800,
        fontSize: 20,
        fontFamily: font,
    };
};

export const questionCard = {
    background: colors.white,
    borderRadius: 20,
    border: '1px solid rgba(5, 150, 105, 0.08)',
    padding: 28,
    marginBottom: 20,
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
};

export const mcqOption = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 20px',
    borderRadius: 14,
    border: isSelected ? '2px solid #059669' : '2px solid rgba(5, 150, 105, 0.15)',
    background: isSelected ? 'rgba(5,150,105,0.05)' : colors.white,
    cursor: 'pointer',
    marginBottom: 10,
    transition: 'all 0.2s ease',
    fontFamily: font,
    fontSize: 15,
});

export const optionRadio = (isSelected) => ({
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: isSelected ? '6px solid #059669' : '2px solid rgba(5, 150, 105, 0.2)',
    flexShrink: 0,
    transition: 'all 0.2s ease',
});

export const navDot = (isActive, isAnswered) => ({
    width: 40,
    height: 40,
    borderRadius: 12,
    border: 'none',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: font,
    background: isActive ? 'linear-gradient(135deg, #059669, #10b981)' :
        isAnswered ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.05)',
    color: isActive ? colors.white : isAnswered ? colors.primary : colors.textSecondary,
    transition: 'all 0.2s ease',
});

// ===== AUTH =====
export const authWrapper = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)',
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
};

export const authCard = {
    background: colors.white,
    borderRadius: 28,
    padding: '44px 40px',
    width: '100%',
    maxWidth: 460,
    boxShadow: '0 25px 80px rgba(5,150,105,0.15)',
    border: '1px solid rgba(5,150,105,0.1)',
    position: 'relative',
    zIndex: 1,
};

export const authBrand = {
    textAlign: 'center',
    marginBottom: 36,
};

export const universityLogo = {
    fontSize: 56,
    display: 'block',
    marginBottom: 16,
    animation: 'float 3s ease-in-out infinite',
};

export const authTitle = {
    fontSize: 26,
    fontWeight: 800,
    color: colors.textPrimary,
    fontFamily: font,
    margin: '0 0 6px',
    letterSpacing: -0.5,
};

export const authSubtitle = {
    fontSize: 15,
    color: colors.primary,
    fontWeight: 600,
    fontFamily: font,
};

export const inputWrapper = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
};

export const inputIcon = {
    position: 'absolute',
    left: 16,
    color: colors.textLight,
    fontSize: 18,
    pointerEvents: 'none',
};

export const authInput = {
    ...input,
    paddingLeft: 46,
};

export const btnAuth = {
    ...btnPrimary,
    width: '100%',
    justifyContent: 'center',
    padding: '16px 24px',
    fontSize: 16,
    borderRadius: 16,
    marginTop: 12,
};

export const authFooter = {
    textAlign: 'center',
    marginTop: 28,
    fontSize: 15,
    color: colors.textSecondary,
    fontFamily: font,
};

export const demoBtn = (variant) => {
    const bg = variant === 'admin' ? 'rgba(245,158,11,0.08)' :
        variant === 'teacher' ? 'rgba(59,130,246,0.08)' : 'rgba(5,150,105,0.08)';
    const color = variant === 'admin' ? '#f59e0b' :
        variant === 'teacher' ? '#3b82f6' : '#059669';
    const border = variant === 'admin' ? 'rgba(245,158,11,0.2)' :
        variant === 'teacher' ? 'rgba(59,130,246,0.2)' : 'rgba(5,150,105,0.2)';
    return {
        padding: '10px 18px',
        borderRadius: 12,
        border: `1px solid ${border}`,
        background: bg,
        color,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: font,
        transition: 'all 0.2s ease',
    };
};

// ===== CONFIRM MODAL =====
export const confirmOverlay = {
    ...modalOverlay,
};

export const confirmModal = {
    background: colors.white,
    borderRadius: 24,
    padding: 36,
    maxWidth: 420,
    textAlign: 'center',
    boxShadow: '0 25px 80px rgba(0,0,0,0.2)',
    border: '1px solid rgba(5, 150, 105, 0.1)',
};

// ===== EVALUATION =====
export const evaluationCard = {
    background: colors.white,
    borderRadius: 20,
    border: '1px solid rgba(5, 150, 105, 0.08)',
    padding: 24,
    marginBottom: 20,
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
};

export const marksInput = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 14,
};

// ===== TEACHER CHECKBOX =====
export const teacherCheckbox = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 18px',
    borderRadius: 14,
    border: '1px solid rgba(5, 150, 105, 0.1)',
    marginBottom: 10,
    cursor: 'pointer',
    fontFamily: font,
    fontSize: 15,
    transition: 'all 0.2s ease',
};

// Export colors for custom use
export { colors, font };
